/**
 * dailyGoalNotification.ts — Notificación nativa con barra de progreso para la
 * Meta del Día (mismo dato que la tarjeta "Meta del Día" del Dashboard),
 * estilo apps de delivery (DiDi/Rappi: "vas en camino", progreso en vivo).
 *
 * QUÉ HACE: Cada vez que se registra una venta (caja o tienda), recalcula el
 *           % de la meta del día y actualiza (in-place, mismo id) una
 *           notificación "en curso" con barra de progreso real de Android.
 * QUÉ INVOCA: computeMetaDia/computeLocalDashboardStats/computeVentasTiendaHoy
 *           (features/metrics — mismos cálculos que usa el Dashboard),
 *           notifications.showGoalProgress (@/lib/platform),
 *           isNotificationCategoryEnabled (store).
 * QUÉ EXPORTA: notifyDailyGoalProgress
 * QUÉ NO DEBE HACER:
 *   - No debe lanzar ni bloquear el registro de auditoría (best-effort).
 *   - Solo tiene efecto real en Android (barra de progreso nativa); en
 *     iOS/Web/Escritorio la llamada de plataforma es un no-op silencioso.
 */
import {
  computeMetaDia,
  computeLocalDashboardStats,
  computeVentasTiendaHoy,
} from "@/features/metrics/services/metrics.service";
import { notifications } from "@/lib/platform";
import { NOTIFICATION_CHANNEL_RULES } from "@/lib/push";
import { isNotificationCategoryEnabled } from "@/store/notificationPrefsStore";
import { txRead, txWrite } from "@/lib/persistence/transactionalStore";

/** Acciones de auditoría que representan una venta cerrada (afectan la meta del día). */
const SALE_ACTIONS = new Set(["caja_venta_completada", "pedido_online"]);

/** ID fijo de la notificación de progreso (se actualiza in-place, no se apila). */
const GOAL_NOTIFICATION_ID = 910001;

/** Clave persistida: última fecha (YYYY-MM-DD) en que ya se mostró la celebración. */
const CELEBRATION_DATE_KEY = "altenot_meta_dia_celebrada_fecha";

function todayKey(): string {
  return new Date().toISOString().slice(0, 10);
}

/** true si ya se celebró la meta de HOY (evita repetir vibración/diseño en cada venta extra). */
function hasCelebratedToday(): boolean {
  return txRead<string>(CELEBRATION_DATE_KEY, "") === todayKey();
}

function markCelebratedToday(): void {
  txWrite(CELEBRATION_DATE_KEY, todayKey());
}

function formatMoney(value: number): string {
  return `$${Math.round(value).toLocaleString("es-CO")}`;
}

/**
 * Si `action` corresponde a una venta, recalcula la meta del día y actualiza
 * la notificación de progreso nativa (best-effort; nunca lanza). La primera
 * vez que se alcanza la meta en el día, usa un diseño festivo (color verde,
 * ícono de celebración, vibración) — las ventas siguientes ya no repiten la
 * celebración, solo actualizan el monto vendido.
 */
export function notifyDailyGoalProgress(action: string): void {
  if (!SALE_ACTIONS.has(action)) return;
  if (!isNotificationCategoryEnabled("ventas")) return;

  try {
    const meta = computeMetaDia();
    const actual =
      computeLocalDashboardStats().ventasHoy.total + computeVentasTiendaHoy();
    if (meta <= 0) return;

    const percentage = Math.min(100, Math.round((actual / meta) * 100));
    const alcanzada = actual >= meta;
    const body = alcanzada
      ? `¡Meta del día alcanzada! Vendiste ${formatMoney(actual)}`
      : `Faltan ${formatMoney(meta - actual)} — Meta: ${formatMoney(meta)}`;

    const celebrate = alcanzada && !hasCelebratedToday();
    if (celebrate) markCelebratedToday();

    notifications
      .showGoalProgress({
        id: GOAL_NOTIFICATION_ID,
        title: `🎯 Meta del día — ${percentage}%`,
        body,
        progress: percentage,
        channelId: NOTIFICATION_CHANNEL_RULES.ventas.channelId,
        ongoing: !alcanzada,
        celebrate,
        deepLinkUri: "altenot://dashboard",
      })
      .catch(() => {
        /* no-op: barra de progreso es best-effort */
      });
  } catch {
    /* no-op: nunca debe romper el registro de auditoría que la dispara */
  }
}
